/**
 * ADR-0166 — Real `BenchmarksAPI` implementation backed by the HTTP client.
 *
 *   getBenchmarks: GET  /v1/sites/:site_id/benchmarks?industry=&volume_band=
 *   optIn:        POST /v1/sites/:site_id/benchmarks/opt-in
 *   optOut:       POST /v1/sites/:site_id/benchmarks/opt-out
 *
 * Result types are discriminated (`ok | forbidden | not_found | error`)
 * so the view layer can branch without a raw `ApiError` leak — mirrors
 * `real-visibility.ts` + `site-auto-refund.ts` house style.
 *
 * The two POST routes return a partial payload on the wire
 * (`opt_in_state`, `opted_in_at`, `opted_out_at` — the fields the state
 * flip touches; the server does NOT recompute cards/breakdown on the
 * mutation). The client factory synthesizes a full `BenchmarksResponse`
 * from the partial by padding `cohort`, `cards`, `category_breakdown` to
 * `null` and `cohort_is_synthetic` to `true` — the view always calls
 * `getBenchmarks` after a successful mutation to reload the six-card
 * grid, so the padded response only serves the type contract.
 */

import type {
  BenchmarkIndustry,
  BenchmarkVolumeBand,
  BenchmarksResponse,
  IsoTimestamp,
  SiteId,
} from '@sill/types';
import type {
  BenchmarksAPI,
  BenchmarksGetResult,
  BenchmarksMutationResult,
} from '@sill/types/api';

import { ApiError, type HttpClient } from './http-client.js';

export interface RealBenchmarksDeps {
  readonly http: HttpClient;
}

interface OptInWire {
  readonly opt_in_state: 'opted_in' | 'opted_out';
  readonly opted_in_at: IsoTimestamp | null;
  readonly opted_out_at: IsoTimestamp | null;
}

function padMutationResponse(wire: OptInWire): BenchmarksResponse {
  // The POST routes return the opt-in state fields only; the view calls
  // `getBenchmarks` after a successful mutation to load the six-card
  // grid. Padding cohort/cards/category_breakdown to `null` satisfies
  // the `BenchmarksResponse` type contract without inventing data.
  return {
    opt_in_state: wire.opt_in_state,
    opted_in_at: wire.opted_in_at,
    opted_out_at: wire.opted_out_at,
    cohort_is_synthetic: true,
    cohort: null,
    cards: null,
    category_breakdown: null,
  };
}

function explainApiError(err: ApiError): string {
  switch (err.kind) {
    case 'service_unavailable':
      return 'Benchmarks service is temporarily unavailable.';
    case 'network_timeout':
      return 'This may have saved — refresh to check before trying again.';
    case 'network_error':
      return 'Network error — check your connection.';
    case 'bad_response':
      return 'Server returned an unexpected response.';
    default:
      return err.message;
  }
}

export function createRealBenchmarksApi(
  deps: RealBenchmarksDeps,
): BenchmarksAPI {
  return {
    async getBenchmarks(
      site_id: SiteId,
      industry: BenchmarkIndustry,
      volume_band: BenchmarkVolumeBand,
    ): Promise<BenchmarksGetResult> {
      const qs = `?industry=${encodeURIComponent(industry)}&volume_band=${encodeURIComponent(volume_band)}`;
      try {
        const res = await deps.http.request<BenchmarksResponse>({
          method: 'GET',
          path: `/v1/sites/${encodeURIComponent(site_id)}/benchmarks${qs}`,
        });
        if (res === undefined) {
          return { kind: 'error', message: 'Empty response body.' };
        }
        return { kind: 'ok', response: res };
      } catch (err) {
        if (err instanceof ApiError) {
          if (err.kind === 'not_found') return { kind: 'not_found' };
          return { kind: 'error', message: explainApiError(err) };
        }
        return {
          kind: 'error',
          message: err instanceof Error ? err.message : 'Network error.',
        };
      }
    },

    async optIn(site_id: SiteId): Promise<BenchmarksMutationResult> {
      try {
        const res = await deps.http.request<OptInWire>({
          method: 'POST',
          path: `/v1/sites/${encodeURIComponent(site_id)}/benchmarks/opt-in`,
          body: {},
        });
        if (res === undefined) {
          return { kind: 'error', message: 'Empty response body.' };
        }
        return { kind: 'ok', response: padMutationResponse(res) };
      } catch (err) {
        if (err instanceof ApiError) {
          if (err.kind === 'forbidden') return { kind: 'forbidden' };
          if (err.kind === 'not_found') return { kind: 'not_found' };
          return { kind: 'error', message: explainApiError(err) };
        }
        return {
          kind: 'error',
          message: err instanceof Error ? err.message : 'Network error.',
        };
      }
    },

    async optOut(site_id: SiteId): Promise<BenchmarksMutationResult> {
      try {
        const res = await deps.http.request<OptInWire>({
          method: 'POST',
          path: `/v1/sites/${encodeURIComponent(site_id)}/benchmarks/opt-out`,
          body: {},
        });
        if (res === undefined) {
          return { kind: 'error', message: 'Empty response body.' };
        }
        return { kind: 'ok', response: padMutationResponse(res) };
      } catch (err) {
        if (err instanceof ApiError) {
          if (err.kind === 'forbidden') return { kind: 'forbidden' };
          if (err.kind === 'not_found') return { kind: 'not_found' };
          return { kind: 'error', message: explainApiError(err) };
        }
        return {
          kind: 'error',
          message: err instanceof Error ? err.message : 'Network error.',
        };
      }
    },
  };
}
